import { Button, FormSelect, Modal } from "@/components";
import { useChangeChatUser } from "@/hooks";
import { useGetChatViewers } from "@/hooks/chat/useGetChatViewers";
import { useDebounce } from "@/hooks/useDebounce";
import { ChatDto } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  ChangeOwnerFormData,
  ChangeOwnerSchema,
} from "../schemas/ChangeOwnerSchema";

interface UpdateChangeRoomOwnerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  roomId: number;
  currentOwner?: ChatDto["created_by"];
}

/**
 * Simple "Hello World" component for UpdateChangeRoomOwner.
 * - Displays current owner
 * - Has a button to toggle between "Hello World" owners for demo purposes
 */
const ChangeRoomOwnerDialog: React.FC<UpdateChangeRoomOwnerProps> = ({
  open,
  setOpen,
  roomId,
  currentOwner,
}) => {
  // save input search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  const { control, handleSubmit, setValue } = useForm({
    resolver: zodResolver(ChangeOwnerSchema),
  });

  // set initial form values
  useEffect(() => {
    if (currentOwner) {
      setValue("owner", {
        label: currentOwner.full_name,
        value: currentOwner.id,
      });
    }
  }, [currentOwner]);

  // Load list of members to select new owner
  const {
    viewers,
    fetchNextPageViewers,
    // hasNextPageViewers,
    isLoadingViewers,
  } = useGetChatViewers(roomId, {
    search: debouncedSearch,
    s: debouncedSearch,
  });

  // handle input in select change
  const handleInputChange = (inputValue: string) => {
    // You can implement search functionality here if needed
    setSearch(inputValue);
  };

  // close dialog
  const handleClose = () => {
    setOpen(false);
  };

  const { changeUser, isChanging } = useChangeChatUser();

  // handle form submit
  const onSubmit = (data: ChangeOwnerFormData) => {
    changeUser({
      id: roomId,
      data: {
        new_user_id: data.owner.value,
      },
    }).then(() => {
      handleClose();
    });
  };

  return (
    <Modal
      open={open}
      onOpenChange={setOpen}
      title="権限を引き継ぐ"
      isStopPropagationOnOverlay={false}
      overflowScroll={false}
    >
      <FormSelect
        label="メンバー名"
        options={viewers}
        control={control}
        name="owner"
        onMenuScrollToBottom={() => fetchNextPageViewers()}
        isLoading={isLoadingViewers}
        isSearchable
        onInputChange={handleInputChange}
      />

      <div className="mt-4 flex items-center gap-2 justify-end">
        <Button type="button" buttonType="text" onClick={handleClose}>
          キャンセル
        </Button>
        <Button
          type="submit"
          buttonType="primary"
          onClick={handleSubmit(onSubmit)}
          isLoading={isChanging}
        >
          確定
        </Button>
      </div>
    </Modal>
  );
};

export default ChangeRoomOwnerDialog;
